/**
 * Формирование plain-text рецепта для шаринга (Telegram, WhatsApp и т.д.).
 * Один текстовый блок «карточка рецепта», без markdown; переносы строк — '\n'.
 */

import type { IngredientItem } from "@/types/recipe";
import { ingredientDisplayLabel } from "@/types/recipe";
import { getAdviceSectionTitle, getShareDescriptionHeading } from "@/utils/infantRecipe";

/** Базовый URL приложения для ссылок в шаринге (рецепт, подпись). Без localhost — всегда прод. */
const BASE_URL = "https://momrecipes.online";

/** URL приложения в подписи шаринга (отдельная константа на случай смены домена). */
export const SHARE_APP_URL = BASE_URL;
/** Строка подписи внизу шаринга (без эмодзи; ссылка — на следующей строке). */
const SHARE_SIGNATURE_LINE = "— Рецепт из приложения Mom Recipes";
const PREP_FALLBACK = "следуйте привычной технологии для этого блюда (в приложении откройте полную версию рецепта).";

const MEAL_EMOJI: Record<string, string> = {
  breakfast: "🥣",
  lunch: "🍲",
  dinner: "🥗",
  snack: "🍪",
};
const MEAL_LABEL: Record<string, string> = {
  breakfast: "Завтрак",
  lunch: "Обед",
  dinner: "Ужин",
  snack: "Перекус",
};

export type MealTypeKey = "breakfast" | "lunch" | "dinner" | "snack";

export interface ShareRecipeInput {
  title: string;
  /** Блок "Почему это полезно" (description). */
  description?: string | null;
  cooking_time_minutes?: number | null;
  recipeId: string;
  /** Уже нормализованный список для отображения (как в UI). */
  ingredients: IngredientItem[];
  /** Шаги приготовления (instruction, step_number). Если один текст — разбить на пункты. */
  steps?: Array<{ instruction?: string; step_number?: number }> | null;
  /** Совет от шефа (chef_advice / chefAdvice). */
  chefAdvice?: string | null;
  /** Для age-aware UX infant-рецептов. */
  max_age_months?: number | null;
  /** Тип приёма пищи: для строки 🥣 Завтрак / 🍲 Обед и т.д. Может быть уже локализованная строка (mealTypeLabel) или ключ. */
  mealTypeLabel?: string | null;
  meal_type?: MealTypeKey | string | null;
  /** URL для подписи (с ep/ch/sr для вирусности). Если не передан — используется BASE_URL. */
  shareUrl?: string | null;
}

function getMealLine(params: Pick<ShareRecipeInput, "mealTypeLabel" | "meal_type">): string | null {
  const { mealTypeLabel, meal_type } = params;
  if (mealTypeLabel != null && String(mealTypeLabel).trim() !== "") {
    const key = meal_type != null && typeof meal_type === "string" ? meal_type.toLowerCase() : null;
    const emoji = key && MEAL_EMOJI[key] ? MEAL_EMOJI[key] : "🍽️";
    return `${emoji} ${String(mealTypeLabel).trim()}`;
  }
  if (meal_type != null && typeof meal_type === "string") {
    const key = meal_type.toLowerCase();
    const label = MEAL_LABEL[key] ?? meal_type;
    const emoji = MEAL_EMOJI[key] ?? "🍽️";
    return `${emoji} ${label}`;
  }
  return null;
}

/** Разбить один текст на пункты по переносам или по точкам. */
function splitInstructionsToSteps(text: string): string[] {
  const trimmed = text.trim();
  if (!trimmed) return [];
  const byNewline = trimmed.split(/\n+/).map((s) => s.trim()).filter(Boolean);
  if (byNewline.length > 1) return byNewline;
  const byDot = trimmed.split(/\.\s+/).map((s) => s.trim()).filter(Boolean);
  if (byDot.length > 1) return byDot.map((s) => (s.endsWith(".") ? s : `${s}.`));
  return [trimmed];
}

function formatPreparationSteps(steps: ShareRecipeInput["steps"]): string {
  if (Array.isArray(steps) && steps.length > 0) {
    const sorted = [...steps].sort(
      (a, b) => (a.step_number ?? 0) - (b.step_number ?? 0)
    );
    return sorted
      .map((s, i) => {
        const num = s.step_number ?? i + 1;
        const text = (s.instruction ?? "").trim();
        return text ? `${num}) ${text}` : "";
      })
      .filter(Boolean)
      .join("\n");
  }
  return "";
}

/**
 * Собирает один текстовый блок рецепта для шаринга в формате «карточки».
 * Порядок: заголовок → тип приёма → время → описание → ингредиенты → шаги → совет → подпись + ссылки.
 */
export function buildRecipeShareText(params: ShareRecipeInput): string {
  const {
    title,
    description,
    cooking_time_minutes,
    recipeId,
    ingredients,
    steps,
    chefAdvice,
    max_age_months,
    mealTypeLabel,
    meal_type,
    shareUrl,
  } = params;

  const lines: string[] = [];
  const blank = () => lines.push("");

  // 1) Заголовок
  lines.push(title.trim() || "Рецепт");

  // 2) Тип приёма пищи
  const mealLine = getMealLine({ mealTypeLabel, meal_type });
  if (mealLine) lines.push(mealLine);

  // 3) Время приготовления (сразу под типом приёма, без дублирования)
  if (cooking_time_minutes != null && Number(cooking_time_minutes) > 0) {
    lines.push(`⏱ ${cooking_time_minutes} мин`);
  }

  // 4) Описание / польза
  if (description != null && String(description).trim() !== "") {
    blank();
    lines.push(getShareDescriptionHeading({ recipe: { max_age_months } }));
    lines.push(String(description).trim());
  }

  // 5) Ингредиенты
  blank();
  lines.push("🧾 Ингредиенты:");
  if (ingredients.length > 0) {
    for (const ing of ingredients) {
      const label = ingredientDisplayLabel(ing);
      if (label) lines.push(`• ${label}`);
    }
  }

  // 6) Шаги приготовления
  blank();
  const stepsFormatted = formatPreparationSteps(steps);
  if (stepsFormatted) {
    lines.push("👩‍🍳 Приготовление:");
    lines.push(stepsFormatted);
  } else {
    lines.push("👩‍🍳 Приготовление: " + PREP_FALLBACK);
  }

  // 7) Совет от шефа
  if (chefAdvice != null && String(chefAdvice).trim() !== "") {
    blank();
    const adviceTitle = getAdviceSectionTitle({
      recipe: { max_age_months },
      kind: "chef",
    });
    lines.push(`👩‍🍳✨ ${adviceTitle}:`);
    lines.push(String(chefAdvice).trim());
  }

  // 8) Подпись + ссылка (shareUrl с ep/ch/sr для аналитики вирусности)
  const body = lines.join("\n");
  const linkUrl = shareUrl && shareUrl.trim() ? shareUrl.trim() : BASE_URL;
  const footer = `${SHARE_SIGNATURE_LINE}\n${linkUrl}`;
  return `${body}\n\n${footer}`;
}

/** Подпись внизу текста шаринга (для тестов). */
export function getShareSignature(): { line: string; url: string } {
  return { line: SHARE_SIGNATURE_LINE, url: SHARE_APP_URL };
}

/**
 * Короткий текст для шаринга рецепта (название + ссылка + ценность).
 * Используется в Telegram и мессенджерах.
 * Ссылка формата https://momrecipes.online/r/{shareRef}
 */
export function buildRecipeShareTextShort(recipeTitle: string, shareUrl: string, description?: string | null): string {
  const title = (recipeTitle || "Рецепт").trim();
  const url = (shareUrl || BASE_URL).trim();
  const benefit =
    description != null && String(description).trim() !== ""
      ? String(description).trim().slice(0, 120)
      : null;
  const lines = [`🍽 ${title}`, ""];
  if (benefit) {
    lines.push(benefit, "");
  }
  lines.push(
    "Посмотреть рецепт:",
    url,
    "",
    "В Mom Recipes можно собрать меню для всей семьи с учётом возраста, аллергий и предпочтений."
  );
  return lines.join("\n");
}

/** Лимит длины для мессенджеров (Telegram ~4096); при превышении — short fallback. */
export const SHARE_TEXT_MAX_LENGTH = 3500;

/** Источник данных рецепта для шаринга (карточка БД или чат). */
export interface RecipeShareSource {
  title: string;
  description?: string | null;
  cooking_time_minutes?: number | null;
  cookingTime?: number | null;
  max_age_months?: number | null;
  meal_type?: string | null;
  mealType?: string | null;
  mealTypeLabel?: string | null;
  ingredients?: Array<IngredientItem | string | Record<string, unknown>>;
  ingredients_items?: IngredientItem[];
  steps?: Array<{ instruction?: string; step_number?: number } | string> | null;
  chefAdvice?: string | null;
  chef_advice?: string | null;
  advice?: string | null;
}

function normalizeShareIngredients(source: RecipeShareSource): IngredientItem[] {
  if (Array.isArray(source.ingredients_items) && source.ingredients_items.length > 0) {
    return source.ingredients_items;
  }
  if (!Array.isArray(source.ingredients) || source.ingredients.length === 0) {
    return [];
  }
  return source.ingredients.map((ing): IngredientItem => {
    if (typeof ing === "string") return { name: ing };
    const o = ing as IngredientItem;
    return o.name != null ? o : { name: String((ing as { name?: string }).name ?? "").trim() };
  });
}

function normalizeShareSteps(
  steps: RecipeShareSource["steps"]
): Array<{ instruction?: string; step_number?: number }> | null {
  if (!Array.isArray(steps) || steps.length === 0) return null;
  return steps.map((s, i) => {
    if (typeof s === "string") {
      return { instruction: s, step_number: i + 1 };
    }
    return s;
  });
}

/**
 * Полная карточка рецепта для шаринга; при длине > SHARE_TEXT_MAX_LENGTH — короткий текст.
 */
export function buildRecipeShareTextForShare(
  source: RecipeShareSource,
  recipeId: string,
  shareUrl: string
): string {
  const title = (source.title || "Рецепт").trim();
  const mealType = source.meal_type ?? source.mealType ?? null;
  const chefAdvice =
    source.chefAdvice ??
    source.chef_advice ??
    source.advice ??
    null;
  const cookingMinutes =
    source.cooking_time_minutes ?? source.cookingTime ?? null;

  const full = buildRecipeShareText({
    title,
    description: source.description,
    cooking_time_minutes: cookingMinutes,
    recipeId,
    ingredients: normalizeShareIngredients(source),
    steps: normalizeShareSteps(source.steps),
    chefAdvice,
    max_age_months: source.max_age_months,
    mealTypeLabel: source.mealTypeLabel,
    meal_type: mealType,
    shareUrl,
  });

  if (full.length <= SHARE_TEXT_MAX_LENGTH) {
    return full;
  }
  return buildRecipeShareTextShort(title, shareUrl, source.description);
}
